import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { removeOwnAvatar } from "../../../api/avatarsApi";
import type { SignedInAccount } from "../../../api/accountsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import { rememberAvatarVersion, useAvatarVersion } from "../../../ui/avatars/avatarVersions";
import AppButton from "../../../ui/buttons/AppButton";
import AvatarCropEditor from "./AvatarCropEditor";

/**
 * The person's picture on their Profile page (F14): choose one and the part shown, or
 * remove it. Without one, the avatar generated for them stands in.
 */
export default function AvatarSettings({ account }: { account: SignedInAccount }) {
  const { t } = useTranslation();
  const version = useAvatarVersion(account.userId);
  const [chosen, setChosen] = useState<File | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const removal = useSubmission();

  if (chosen !== null) {
    return (
      <AvatarCropEditor
        file={chosen}
        onSaved={(saved) => {
          rememberAvatarVersion(account.userId, saved);
          setChosen(null);
        }}
        onCancel={() => { setChosen(null); }}
      />
    );
  }
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <AppButton size="sm" onClick={() => picker.current?.click()}>
          {version === null ? t("profile.avatar.choose") : t("profile.avatar.change")}
        </AppButton>
        {version === null ? null : (
          <AppButton
            size="sm"
            variant="danger"
            disabled={removal.state.status === "submitting"}
            onClick={() => {
              removal.submit(async () => {
                await removeOwnAvatar();
                rememberAvatarVersion(account.userId, null);
              });
            }}
          >
            {t("profile.avatar.remove")}
          </AppButton>
        )}
      </div>
      <p className="app-help-text">{t("profile.avatar.hint")}</p>
      {removal.state.status === "failed" ? <ErrorNotice error={removal.state.error} compact /> : null}
      <input
        ref={picker}
        type="file"
        accept="image/*"
        hidden
        tabIndex={-1}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file !== undefined) setChosen(file);
        }}
      />
    </div>
  );
}
